import React, { useContext, useRef } from "react";
import type { AriaRadioProps } from "react-aria";
import { mergeProps, useFocusRing, useRadio } from "react-aria";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";

import { RadioGroupContext } from "./context/radio-group-context";
import type { SlotClassNames } from "./types";

const radio = tv({
  slots: {
    base: "flex cursor-pointer items-center gap-2 data-disabled:opacity-50",
    indicator: `border-border grid size-4 place-items-center rounded-full border transition *:scale-0 peer-checked:*:scale-100`,
    dot: "bg-primary size-2 rounded-full transition-transform duration-300",
    label: "text-text-primary",
  },
  variants: {
    isFocusVisible: {
      true: { indicator: "ring-1 ring-primary/50 ring-offset-1" },
    },
  },
});

export type RadioProps = Omit<AriaRadioProps, "children"> & {
  readonly children?: React.ReactNode;
  readonly className?: string;
  readonly classNames?: SlotClassNames<ReturnType<typeof radio>>;
};

export default function Radio({
  children,
  className,
  classNames,
  ...props
}: RadioProps) {
  const state = useContext(RadioGroupContext);

  if (!state)
    throw new Error("RadioGroup must be used within a RadioGroupContext");

  const ref = useRef<HTMLInputElement>(null);
  const { inputProps, labelProps } = useRadio(
    { ...props, children },
    state,
    ref,
  );
  const { isFocusVisible, focusProps } = useFocusRing();

  const isDisabled = inputProps.disabled;
  const styles = radio({ isFocusVisible });

  return (
    <label
      className={styles.base({
        className: twMerge(className, classNames?.base),
      })}
      data-disabled={isDisabled ? "true" : undefined}
    >
      <input
        ref={ref}
        className="peer sr-only"
        aria-labelledby={labelProps.id}
        {...mergeProps(inputProps, focusProps)}
      />

      <span
        className={styles.indicator({ className: classNames?.indicator })}
        aria-hidden
      >
        <span className={styles.dot({ className: classNames?.dot })} />
      </span>

      {children && (
        <span
          className={styles.label({ className: classNames?.label })}
          {...labelProps}
        >
          {children}
        </span>
      )}
    </label>
  );
}
